'use client'
import { useIsTabletOrMobileScreen } from '@components/hooks/useIsMobileScreen'
import type { Url } from '@lib/routes'
import absoluteUrl from '@lib/routes/helpers/absoluteUrl'
import urlWithQueryParams from '@lib/routes/helpers/urlWithQueryParams'
import deleteFacetFilterFromSlug from '@modules/trials/search/facetFilters/deleteFacetFilterFromSlug'
import updateFacetFilterFromSlug from '@modules/trials/search/facetFilters/updateFacetFilterFromSlug'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useCallback } from 'react'

export function useHandleFacetClick() {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = new URLSearchParams(useSearchParams() ?? '')
  const isMobile = useIsTabletOrMobileScreen()

  // Changing a facet should reset the page number
  if (searchParams.has('page')) {
    searchParams.delete('page')
  }

  const navigateWithFilters = useCallback(
    (url: Url | undefined) => {
      if (url) {
        router.push(
          absoluteUrl(
            urlWithQueryParams(url.toString(), {
              ...(isMobile ? { showFilters: 'true' } : {}),
              ...Object.fromEntries(searchParams),
            }),
          ),
        )
      }
    },
    [router, searchParams],
  )

  const handleFacetClick = ({
    facetType,
    selectedValue,
    setSelectedValue,
    value,
  }: {
    facetType: 'condition' | 'treatment'
    selectedValue: string | undefined
    setSelectedValue: (value: string | undefined) => void
    value: string
  }) => {
    if (selectedValue === value) {
      setSelectedValue(undefined)
      const url = deleteFacetFilterFromSlug({
        facet: facetType,
        slug: pathname,
      })
      navigateWithFilters(url)
    } else {
      setSelectedValue(value)
      const url = updateFacetFilterFromSlug({
        facet: facetType,
        slug: pathname,
        value: value,
      })
      navigateWithFilters(url)
    }
  }

  return handleFacetClick
}
